import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { trpc } from "@/lib/trpc";
import { formatInstallationReference } from "@shared/finance/manualExpenses";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ExternalLink,
  FileDown,
} from "lucide-react";
import { useState } from "react";
import { Link, useLocation, useSearch } from "wouter";
import {
  ALLOWANCES_XLSX_MIME_TYPE,
  allowanceTicketLabel,
  buildAllowancesXlsx,
  mapAllowanceExportRows,
  sortAllowanceRows,
} from "./allowancesExport";
import {
  getAllowanceStatusFromSearch,
  getAllowanceStatusPath,
  getAllowanceStatusQuery,
} from "@/finance/dashboardQueues";

const TECHNICIAN_CATEGORY_TO_FINANCIAL_CATEGORY: Readonly<
  Record<string, string>
> = {
  fuel: "fleet_fuel",
  toll: "travel_allowances",
  parking: "travel_allowances",
  hotel: "travel_allowances",
  meal: "travel_allowances",
  vehicle_cleaning: "fleet_fuel",
  store_purchase: "materials",
  other: "other",
} as const;

const CATEGORY_LABELS: Record<string, string> = {
  fuel: "Combustible",
  toll: "Peaje",
  parking: "Parking",
  hotel: "Hotel",
  meal: "Comida",
  vehicle_cleaning: "Limpieza vehículo",
  store_purchase: "Compra tienda",
  other: "Otro",
};

const FINANCIAL_CATEGORY_LABELS: Record<string, string> = {
  materials: "Materiales",
  subcontractors: "Subcontratas",
  transport: "Transporte y portes",
  rentals: "Alquileres",
  professional_services: "Servicios profesionales",
  utilities: "Suministros",
  insurance: "Seguros",
  taxes_fees: "Impuestos y tasas",
  travel_allowances: "Dietas y desplazamientos",
  fleet_fuel: "Vehículos y combustible",
  tools_ppe: "Herramientas y EPIs",
  warehouse: "Almacén",
  other: "Otros",
};

const STATUS_LABELS: Record<string, string> = {
  pending: "Pendiente",
  approved: "Aprobado",
  rejected: "Rechazado",
  pending_invoicing: "Pdte. facturar",
  invoiced: "Facturado",
  void: "Anulado",
};

const STATUS_COLORS: Record<string, string> = {
  pending: "bg-yellow-100 text-yellow-800",
  approved: "bg-green-100 text-green-800",
  rejected: "bg-red-100 text-red-800",
  pending_invoicing: "bg-blue-100 text-blue-800",
  invoiced: "bg-gray-100 text-gray-700",
  void: "bg-gray-100 text-gray-400 line-through",
};

const euro = new Intl.NumberFormat("es-ES", {
  style: "currency",
  currency: "EUR",
});

type AllowanceSortKey =
  | "expediente"
  | "cliente"
  | "tecnico"
  | "categoria"
  | "factura"
  | "fecha"
  | "proveedor"
  | "importe"
  | "estado"
  | "enlace";

type AllowanceSort = { key: AllowanceSortKey; direction: "asc" | "desc" };

function getFinancialCategoryName(technicianCategory: string): string {
  const financialCategoryCode =
    TECHNICIAN_CATEGORY_TO_FINANCIAL_CATEGORY[technicianCategory];
  return financialCategoryCode
    ? FINANCIAL_CATEGORY_LABELS[financialCategoryCode] || financialCategoryCode
    : CATEGORY_LABELS[technicianCategory] || technicianCategory;
}

export function AllowancesTab() {
  const searchString = useSearch();
  const [, setLocation] = useLocation();
  const filterStatus = getAllowanceStatusFromSearch(searchString);
  const [filterCategory, setFilterCategory] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<AllowanceSort | null>(null);

  const query = trpc.expenses.listForFinance.useQuery({
    ...getAllowanceStatusQuery(filterStatus),
    category: filterCategory !== "all" ? filterCategory : undefined,
  });

  const allExpenses: any[] = query.data ?? [];
  const expenses = (
    search
      ? allExpenses.filter(
          e =>
            e.vendor?.toLowerCase().includes(search.toLowerCase()) ||
            e.documentNumber?.toLowerCase().includes(search.toLowerCase()) ||
            e.installationReference?.installationCode
              ?.toLowerCase()
              .includes(search.toLowerCase()) ||
            e.installationReference?.expedienteCode
              ?.toLowerCase()
              .includes(search.toLowerCase()) ||
            e.installationReference?.clientId
              ?.toLowerCase()
              .includes(search.toLowerCase()) ||
            e.installationReference?.clientName
              ?.toLowerCase()
              .includes(search.toLowerCase()) ||
            e.installationReference?.siteName
              ?.toLowerCase()
              .includes(search.toLowerCase()) ||
            e.installationReference?.workDescription
              ?.toLowerCase()
              .includes(search.toLowerCase()) ||
            e.installationReference?.address
              ?.toLowerCase()
              .includes(search.toLowerCase()) ||
            e.installationName?.toLowerCase().includes(search.toLowerCase()) ||
            e.uploadedByName?.toLowerCase().includes(search.toLowerCase())
        )
      : allExpenses
  ).map(expense => ({
    ...expense,
    installationReference: expense.installationReference
      ? {
          ...expense.installationReference,
          // clientId is a search alias, never a visible expediente label.
          expedienteCode:
            expense.installationReference.installationCode ?? null,
        }
      : null,
  }));

  const totalAmount = expenses.reduce(
    (sum, e) => sum + parseFloat(e.amount ?? "0"),
    0
  );

  const sortedExpenses = sort
    ? sortAllowanceRows(expenses, sort.direction, expense => {
        switch (sort.key) {
          case "expediente":
            return expense.installationReference
              ? expense.installationReference.installationCode
                ? `Exp. ${expense.installationReference.installationCode}`
                : "Expediente sin código"
              : "—";
          case "cliente":
            return expense.installationReference?.clientName ?? "—";
          case "tecnico":
            return expense.uploadedByName ?? "—";
          case "categoria":
            return getFinancialCategoryName(expense.category);
          case "factura":
            return allowanceTicketLabel(expense.documentNumber);
          case "fecha":
            return expense.date ?? "";
          case "proveedor":
            return expense.vendor ?? "—";
          case "importe":
            return Number.parseFloat(String(expense.amount ?? "0")) || 0;
          case "estado":
            return STATUS_LABELS[expense.status] ?? expense.status;
          case "enlace":
            return expense.installationId ?? -1;
        }
      })
    : expenses;

  const requestSort = (key: AllowanceSortKey) => {
    setSort(current =>
      current?.key === key
        ? { key, direction: current.direction === "desc" ? "asc" : "desc" }
        : { key, direction: "desc" }
    );
  };

  const renderSortHeader = (
    key: AllowanceSortKey,
    label: string,
    align: "left" | "right" = "left"
  ) => {
    const direction = sort?.key === key ? sort.direction : null;
    const Icon = !direction
      ? ArrowUpDown
      : direction === "desc"
        ? ArrowDown
        : ArrowUp;
    const nextDirection =
      direction === "desc" ? "de menor a mayor" : "de mayor a menor";
    return (
      <th
        scope="col"
        aria-sort={
          direction
            ? direction === "desc"
              ? "descending"
              : "ascending"
            : "none"
        }
        className={`px-3 py-2 font-medium ${align === "right" ? "text-right" : "text-left"}`}
      >
        <button
          type="button"
          onClick={() => requestSort(key)}
          aria-label={`Ordenar ${label} ${nextDirection}`}
          className={`inline-flex items-center gap-1 hover:text-foreground focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${align === "right" ? "ml-auto" : ""}`}
        >
          <span>{label}</span>
          <Icon aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
        </button>
      </th>
    );
  };

  const exportExpenses = () => {
    const rows = mapAllowanceExportRows(
      sortedExpenses,
      expense => {
        if (!expense.installationReference) return "—";
        return expense.installationReference.installationCode
          ? `Exp. ${expense.installationReference.installationCode}`
          : "Expediente sin código";
      },
      getFinancialCategoryName,
      STATUS_LABELS
    );
    const workbook = buildAllowancesXlsx(rows);
    const workbookBuffer = workbook.buffer.slice(
      workbook.byteOffset,
      workbook.byteOffset + workbook.byteLength
    ) as ArrayBuffer;
    const objectUrl = URL.createObjectURL(
      new Blob([workbookBuffer], { type: ALLOWANCES_XLSX_MIME_TYPE })
    );
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = "gastos-pendientes.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1_000);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          placeholder="Buscar N.º expediente, cliente, trabajo, dirección, proveedor, N.º factura o técnico"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="max-w-xs"
        />
        <Select
          value={filterStatus}
          onValueChange={value =>
            setLocation(getAllowanceStatusPath(searchString, value))
          }
        >
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            <SelectItem value="pending-review">
              Pendientes de revisión
            </SelectItem>
            {Object.entries(STATUS_LABELS).map(([k, v]) => (
              <SelectItem key={k} value={k}>
                {v}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-[170px]">
            <SelectValue placeholder="Categoría" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las categorías</SelectItem>
            {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
              <SelectItem key={k} value={k}>
                {v}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <button
          type="button"
          onClick={exportExpenses}
          disabled={expenses.length === 0}
          className="inline-flex items-center rounded-md border px-3 py-2 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50"
        >
          <FileDown className="mr-2 h-4 w-4" />
          Exportar a Excel
        </button>
      </div>

      {filterStatus === "pending-review" && (
        <p
          role="status"
          className="rounded-md border border-primary/20 bg-primary/5 px-3 py-2 text-sm text-muted-foreground"
        >
          Gastos de técnicos pendientes de revisión administrativa.
        </p>
      )}

      {expenses.length > 0 && (
        <p className="text-sm text-muted-foreground">
          {expenses.length} gasto{expenses.length !== 1 ? "s" : ""} · Total:{" "}
          <span className="font-medium text-foreground">
            {euro.format(totalAmount)}
          </span>
        </p>
      )}

      {query.isLoading && (
        <Card>
          <CardContent className="py-8 text-center text-sm text-muted-foreground">
            Cargando gastos...
          </CardContent>
        </Card>
      )}

      {query.isError && (
        <Card role="alert">
          <CardContent className="py-8 text-center text-sm text-destructive">
            No se pudieron cargar los gastos. Vuelve a intentarlo.
          </CardContent>
        </Card>
      )}

      {!query.isLoading && !query.isError && expenses.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Sin gastos registrados para los filtros seleccionados.
          </CardContent>
        </Card>
      )}

      {expenses.length > 0 && (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/40">
              <tr>
                {renderSortHeader("expediente", "N.º expediente")}
                {renderSortHeader("cliente", "Cliente")}
                {renderSortHeader("tecnico", "Técnico")}
                {renderSortHeader("categoria", "Categoría")}
                {renderSortHeader("factura", "N.º factura o ticket")}
                {renderSortHeader("fecha", "Fecha")}
                {renderSortHeader("proveedor", "Proveedor")}
                {renderSortHeader("importe", "Importe", "right")}
                {renderSortHeader("estado", "Estado")}
                {renderSortHeader("enlace", "Enlace")}
              </tr>
            </thead>
            <tbody>
              {sortedExpenses.map(e => (
                <tr
                  key={e.id}
                  className="border-b last:border-0 hover:bg-muted/30"
                >
                  <td className="px-3 py-2">
                    {e.installationReference ? (
                      <Link
                        href={`/installations/${e.installationReference.id}`}
                        className="font-medium text-blue-600 hover:underline"
                        title={
                          e.installationReference
                            ? formatInstallationReference(
                                e.installationReference
                              )
                            : ""
                        }
                      >
                        {e.installationReference.installationCode
                          ? `Exp. ${e.installationReference.installationCode}`
                          : "Expediente sin código"}
                      </Link>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td
                    className="max-w-[180px] truncate px-3 py-2"
                    title={e.installationReference?.clientName ?? ""}
                  >
                    {e.installationReference?.clientName ?? "—"}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {e.uploadedByName ?? "—"}
                  </td>
                  <td className="px-3 py-2">
                    {getFinancialCategoryName(e.category)}
                  </td>
                  <td className="px-3 py-2">
                    {allowanceTicketLabel(e.documentNumber)}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">{e.date}</td>
                  <td
                    className="max-w-[150px] truncate px-3 py-2"
                    title={e.vendor ?? ""}
                  >
                    {e.vendor ?? "—"}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {euro.format(parseFloat(e.amount ?? "0"))}
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[e.status] ?? ""}`}
                    >
                      {STATUS_LABELS[e.status] ?? e.status}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    {e.installationId ? (
                      <Link
                        href={`/installations/${e.installationId}/materials?tab=expenses&expenseId=${e.id}&showInvoiced=1`}
                        className="flex items-center gap-1 text-blue-600 hover:underline"
                        title={
                          e.installationReference
                            ? formatInstallationReference(
                                e.installationReference
                              )
                            : ""
                        }
                      >
                        <span>Ver instalación</span>
                        <ExternalLink className="h-3 w-3 shrink-0" />
                      </Link>
                    ) : (
                      "—"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
